import { calendarDate, sumMoney } from './financialMath.js';
import { accountingEntries, summarizeTransactions } from './financialLedger.js';
import { format } from 'date-fns';

class ExportService {
  /**
   * Export transactions to CSV format
   */
  exportTransactionsToCSV(transactions, settings = {}) {
    if (!transactions || transactions.length === 0) {
      throw new Error('No transactions to export');
    }

    const currency = settings.currency || 'AUD';
    
    // CSV headers
    const headers = [
      'Date',
      'Type',
      'Amount',
      'Category',
      'Payee',
      'Notes',
      'Tags',
      'Account',
      'Person',
      'Status',
      'Created At',
      'Updated At'
    ];

    // Convert transactions to CSV rows
    const rows = transactions.map(transaction => [
      calendarDate(transaction.date),
      transaction.type,
      transaction.amount,
      transaction.category || '',
      transaction.payee || '',
      transaction.notes || '',
      Array.isArray(transaction.tags) ? transaction.tags.join('; ') : (transaction.tags || ''),
      transaction.account || '',
      transaction.person || '',
      transaction.status || 'posted',
      transaction.createdAt ? format(new Date(transaction.createdAt), 'yyyy-MM-dd HH:mm:ss') : '',
      transaction.updatedAt ? format(new Date(transaction.updatedAt), 'yyyy-MM-dd HH:mm:ss') : ''
    ]);

    // Combine headers and rows
    const csvContent = [headers, ...rows]
      .map(row => row.map(field => `"${String(field).replace(/"/g, '""')}"`).join(','))
      .join('\n');

    return csvContent;
  }

  /**
   * Export transactions to JSON format
   */
  exportTransactionsToJSON(transactions, settings = {}) {
    if (!transactions || transactions.length === 0) {
      throw new Error('No transactions to export');
    }

    const exportData = {
      exportDate: new Date().toISOString(),
      version: '2.0.0',
      currency: settings.currency || 'AUD',
      totalTransactions: transactions.length,
      summary: this.generateTransactionsSummary(transactions),
      transactions: transactions.map(transaction => ({
        ...transaction,
        // Ensure dates are properly formatted
        date: calendarDate(transaction.date),
        createdAt: transaction.createdAt ? new Date(transaction.createdAt).toISOString() : null,
        updatedAt: transaction.updatedAt ? new Date(transaction.updatedAt).toISOString() : null,
      }))
    };

    return JSON.stringify(exportData, null, 2);
  }

  /**
   * Generate summary statistics for transactions
   */
  generateTransactionsSummary(transactions) {
    // A transaction export contains raw source entries; the summary applies
    // accrual accounting and excludes any linked loan transfers.
    const entries = accountingEntries(transactions, 'accrual');
    const totals = summarizeTransactions(entries);
    const income = entries.filter(t => t.type === 'income');
    const expenses = entries.filter(t => t.type === 'expense');
    const categories = [...new Set(entries.map(t => t.category))];
    const dates = entries.map(t => calendarDate(t.date)).filter(Boolean).sort();
    return {
      accountingBasis: 'accrual',
      totalIncome: totals.income,
      totalExpenses: totals.expenses,
      netAmount: totals.net,
      incomeTransactions: income.length,
      expenseTransactions: expenses.length,
      uniqueCategories: categories.length,
      categories,
      dateRange: {earliest:dates[0] || null,latest:dates[dates.length-1] || null},
      averageTransactionAmount: entries.length
        ? (totals.income + totals.expenses) / entries.length : 0
    };
  }

  /**
   * Download file to user's device
   */
  async downloadFile(content, filename, contentType = 'text/plain') {
    const blob = new Blob([content], { type: contentType });

    // Check if File System Access API is available (Chrome/Edge)
    if ('showSaveFilePicker' in window) {
      try {
        const fileExtension = filename.split('.').pop();
        const fileHandle = await window.showSaveFilePicker({
          suggestedName: filename,
          types: [{
            description: `${fileExtension.toUpperCase()} files`,
            accept: { [contentType]: [`.${fileExtension}`] },
          }],
        });
        
        const writable = await fileHandle.createWritable();
        await writable.write(blob);
        await writable.close();
        
        return { success: true, method: 'fileSystemAccess' };
      } catch (err) {
        if (err.name === 'AbortError') {
          return { success: false, cancelled: true };
        }
        // Fall through to traditional download
      }
    }

    // Fallback for other browsers or if File System Access fails
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    return { success: true, method: 'traditional' };
  }

  /**
   * Generate filename with timestamp
   */
  generateFilename(prefix, extension, includeTimestamp = true) {
    const timestamp = includeTimestamp ? 
      `-${format(new Date(), 'yyyy-MM-dd-HHmm')}` : '';
    return `${prefix}${timestamp}.${extension}`;
  }

  /**
   * Export transactions with format selection
   */
  async exportTransactions(transactions, format = 'csv', settings = {}) {
    if (!transactions || transactions.length === 0) {
      throw new Error('No transactions to export');
    }

    let content, contentType, filename;

    switch (format.toLowerCase()) {
      case 'csv':
        content = this.exportTransactionsToCSV(transactions, settings);
        contentType = 'text/csv';
        filename = this.generateFilename('transactions', 'csv');
        break;
        
      case 'json':
        content = this.exportTransactionsToJSON(transactions, settings);
        contentType = 'application/json';
        filename = this.generateFilename('transactions', 'json');
        break;
        
      default:
        throw new Error(`Unsupported export format: ${format}`);
    }

    const result = await this.downloadFile(content, filename, contentType);
    
    if (result.success) {
      return {
        success: true,
        filename,
        format,
        transactionCount: transactions.length,
        method: result.method
      };
    } else if (result.cancelled) {
      return {
        success: false,
        cancelled: true
      };
    } else {
      throw new Error('Failed to download file');
    }
  }

  /**
   * Export filtered transactions from UI state
   */
  async exportFilteredTransactions(allTransactions, filters, selectedTransactions, format = 'csv', settings = {}) {
    // Determine which transactions to export
    let transactionsToExport;
    
    if (selectedTransactions && selectedTransactions.length > 0) {
      // Export only selected transactions
      transactionsToExport = allTransactions.filter(t => 
        selectedTransactions.includes(t.id)
      );
    } else {
      // Export all filtered transactions
      transactionsToExport = this.applyFilters(allTransactions, filters);
    }

    if (transactionsToExport.length === 0) {
      throw new Error('No transactions match the current filters');
    }

    return this.exportTransactions(transactionsToExport, format, settings);
  }

  /**
   * Apply filters to transactions (same logic as in Transactions component)
   */
  applyFilters(transactions, filters) {
    return transactions.filter(transaction => {
      // Date range filter
      if (filters.dateRange) {
        const transactionDate = new Date(transaction.date);
        const { start, end } = filters.dateRange;
        if (transactionDate < start || transactionDate > end) {
          return false;
        }
      }

      // Category filter
      if (filters.category && transaction.category !== filters.category) {
        return false;
      }

      // Person filter
      if (filters.person && transaction.person !== filters.person) {
        return false;
      }

      // Type filter
      if (filters.type && transaction.type !== filters.type) {
        return false;
      }

      // Search text filter
      if (filters.searchText) {
        const searchLower = filters.searchText.toLowerCase();
        const searchableText = [
          transaction.payee,
          transaction.notes,
          transaction.category,
          Array.isArray(transaction.tags) ? transaction.tags.join(' ') : transaction.tags,
        ].join(' ').toLowerCase();
        
        if (!searchableText.includes(searchLower)) {
          return false;
        }
      }

      return true;
    });
  }

  /**
   * Export categories to CSV
   */
  exportCategoriesToCSV(categories) {
    if (!categories || categories.length === 0) {
      throw new Error('No categories to export');
    }

    const headers = ['Name', 'Type', 'Color', 'Icon', 'Created At', 'Updated At'];
    
    const rows = categories.map(category => [
      category.name,
      category.type,
      category.color || '',
      category.icon || '',
      category.createdAt ? format(new Date(category.createdAt), 'yyyy-MM-dd HH:mm:ss') : '',
      category.updatedAt ? format(new Date(category.updatedAt), 'yyyy-MM-dd HH:mm:ss') : ''
    ]);

    const csvContent = [headers, ...rows]
      .map(row => row.map(field => `"${String(field).replace(/"/g, '""')}"`).join(','))
      .join('\n');

    return csvContent;
  }

  /**
   * Export budgets to CSV
   */
  exportBudgetsToCSV(budgets) {
    if (!budgets || budgets.length === 0) {
      throw new Error('No budgets to export');
    }

    const headers = ['Category', 'Amount', 'Period', 'Start Date', 'End Date', 'Created At', 'Updated At'];
    
    const rows = budgets.map(budget => [
      budget.category,
      budget.amount,
      budget.period || 'monthly',
      budget.startDate ? format(new Date(budget.startDate), 'yyyy-MM-dd') : '',
      budget.endDate ? format(new Date(budget.endDate), 'yyyy-MM-dd') : '',
      budget.createdAt ? format(new Date(budget.createdAt), 'yyyy-MM-dd HH:mm:ss') : '',
      budget.updatedAt ? format(new Date(budget.updatedAt), 'yyyy-MM-dd HH:mm:ss') : ''
    ]);

    const csvContent = [headers, ...rows]
      .map(row => row.map(field => `"${String(field).replace(/"/g, '""')}"`).join(','))
      .join('\n');

    return csvContent;
  }
}

export const exportService = new ExportService();


// FRONTEND REPORTING ONLY. Never accesses PocketBase, Google APIs or IndexedDB.
// The application passes its already customer-scoped, loaded React state.
import {selectAccountingEntries,monthlyBudgetCents,personLedger} from './financialLedger.js';
import {toCents,fromCents} from './financialMath.js';

export const REPORT_TYPES=[
 {id:'transactions',name:'Transactions Ledger',description:'Detailed income and expense ledger for the selected period',icon:'FiFileText'},
 {id:'recurring',name:'Recurring Transactions',description:'Recurring rules, schedules and commitments',icon:'FiRepeat'},
 {id:'categories',name:'Categories List',description:'Category activity, transaction counts and totals',icon:'FiGrid'},
 {id:'budget',name:'Budget vs Actual',description:'Budget allocations compared with recognised spending',icon:'FiBarChart2'},
 {id:'spending',name:'Spending by Category',description:'How expenses are distributed across categories',icon:'FiPieChart'},
 {id:'monthly',name:'Monthly Financial Summary',description:'Income, expenses, net movement and key figures',icon:'FiCalendar'},
 {id:'cashflow',name:'Cash Flow Summary',description:'Posted payments and receipts, by month',icon:'FiActivity'},
 {id:'tax',name:'Tax Preparation Expense Register',description:'Posted expenses to help prepare records for an adviser',icon:'FiClipboard'},
 {id:'people',name:'People & IOU Summary',description:'Person balances and recorded IOU activity',icon:'FiUsers'},
 {id:'trend',name:'Income vs Expense Trend',description:'Monthly movement and net position over time',icon:'FiTrendingUp'}
];
const num=x=>Number(x)||0;
const addMoney=(list,get=x=>x.amount)=>fromCents(list.reduce((n,x)=>n+toCents(num(get(x))),0));
const iso=v=>calendarDate(v);
const monthKeys=(start,end)=>{
 const a=iso(start),b=iso(end),result=[];
 if(!a||!b||a>b)throw new Error('Select a valid report period.');
 let year=Number(a.slice(0,4)),month=Number(a.slice(5,7));
 while(year*12+month<=Number(b.slice(0,4))*12+Number(b.slice(5,7))){
   result.push(String(year)+'-'+String(month).padStart(2,'0'));
   if(++month>12){month=1;year++}
   if(result.length>120)throw new Error('Report period is too long.');
 }
 return result;
};
const nameOf=(value,categories)=>{
 const cat=categories.find(c=>String(c.id)===String(value)||String(c.name)===String(value));
 return cat?.name||String(value||'Uncategorised');
};
const asReport=(id,columns,rows,options={})=>{
 const meta=REPORT_TYPES.find(x=>x.id===id);
 return {...meta,columns,rows,notes:options.notes||[],chart:options.chart||[],
   kpis:options.kpis||[],tableTitle:options.tableTitle||meta.name,dashboard:options.dashboard||null};
};
const sumBy=(entries,key,get=x=>x.amount)=>{
 const out=new Map();
 entries.forEach(e=>out.set(key(e),fromCents(toCents(out.get(key(e))||0)+toCents(num(get(e))))));
 return out;
};

export function buildFinancialReports({
 transactions=[],categories=[],budgets=[],people=[],ious=[],recurringRules=[],
 settings={},mode='accrual',view='current',account='',start,end,asOf=new Date()
}={}){
 const from=iso(start),to=iso(end);
 if(!from||!to||from>to)throw new Error('Please choose a valid report period.');
 const allAccounts=account?transactions.filter(t=>String(t.account||'')===String(account)):transactions;
 const safeCategories=categories.filter(c=>!c.isDeleted);
 const basis=mode==='cash'?'cash':'accrual';
 const raw=accountingEntries(allAccounts,basis,ious);
 const selected=selectAccountingEntries(raw,{start:from,end:to,asOf,view,mode:basis});
 const totals=summarizeTransactions(selected);
 const expense=selected.filter(t=>t.type==='expense');
 const income=selected.filter(t=>t.type==='income');
 const months=monthKeys(from,to);
 const categoryMap=new Map();
 selected.forEach(t=>{
   const name=nameOf(t.category,safeCategories),key=t.type+':'+name;
   const old=categoryMap.get(key)||{name,type:t.type,count:0,cents:0};
   old.count++;old.cents+=toCents(t.amount);categoryMap.set(key,old);
 });
 const expCategory=[...categoryMap.values()].filter(x=>x.type==='expense')
   .sort((a,b)=>b.cents-a.cents).map(x=>({...x,amount:fromCents(x.cents)}));
 const spentTotal=addMoney(expense);
 const monthRows=months.map(key=>{
   const subset=selected.filter(t=>iso(t.date).slice(0,7)===key);
   const subtot=summarizeTransactions(subset);
   return [key,subtot.income,subtot.expenses,subtot.net,subtot.transactions];
 });
 const monthChart=monthRows.map(r=>({name:r[0],primary:r[1],secondary:r[2]}));
 const dailyChart=[];
 if(months.length===1){
   const cursor=new Date(from+'T12:00:00Z'),last=new Date(to+'T12:00:00Z');
   for(let i=0;cursor<=last&&i<35;i++,cursor.setUTCDate(cursor.getUTCDate()+1)){
     const key=cursor.toISOString().slice(0,10);
     const day=summarizeTransactions(selected.filter(t=>iso(t.date)===key));
     dailyChart.push({name:key.slice(8),primary:day.income,secondary:day.expenses});
   }
 }
 const spendChart=expCategory.slice(0,6).map(x=>({name:x.name,primary:x.amount}));
 const asOfDate=iso(asOf);
 const asOfCutoff=view==='future'?to:(to<asOfDate?to:asOfDate);
 const today=new Date(asOf);
 const dateFromKey=key=>new Date(Number(key.slice(0,4)),Number(key.slice(5,7))-1,15,12);
 const accrual=selectAccountingEntries(accountingEntries(allAccounts,'accrual',ious),
   {start:from,end:to,asOf,view,mode:'accrual'}).filter(t=>t.type==='expense');
 const budgetRows=budgets.filter(b=>b&&!b.isDeleted&&b.type!=='income').map(b=>{
   const allocated=addMoney(months.map(key=>({amount:fromCents(monthlyBudgetCents(b,dateFromKey(key),settings.financialYearStart||'07-01'))})));
   const spent=addMoney(accrual.filter(t=>String(t.category)===String(b.category)&&
     (b.period!=='custom'||(iso(b.startDate)&&iso(b.endDate)&&iso(t.date)>=iso(b.startDate)&&iso(t.date)<=iso(b.endDate)))));
   const remaining=fromCents(toCents(allocated)-toCents(spent));
   return [nameOf(b.category,safeCategories),String(b.period||'monthly'),allocated,spent,remaining,
     allocated>0?Math.round(spent/allocated*1000)/10:null];
 });
 const budgetLimit=addMoney(budgetRows,r=>r[2]),budgetUsed=addMoney(budgetRows,r=>r[3]);
 const budgetChart=budgetRows.slice(0,6).map(r=>({name:r[0],primary:r[2],secondary:r[3]}));
 const posted=selectAccountingEntries(accountingEntries(allAccounts,'cash',ious),
   {start:from,end:to,asOf,view:'current',mode:'cash'});
 const cashMonths=months.map(key=>{
   const total=summarizeTransactions(posted.filter(t=>iso(t.date).slice(0,7)===key));
   return [key,total.income,total.expenses,total.net,total.transactions];
 });
 const seenCategories=new Set(safeCategories.map(c=>String(c.id)));
 const categoryRows=safeCategories.map(c=>{
   const records=selected.filter(t=>String(t.category)===String(c.id)||String(t.category)===String(c.name));
   return [c.name,c.type,records.length,addMoney(records),c.color||''];
 });
 const recurringRows=recurringRules.filter(r=>r&&!r.isDeleted).map(r=>
   [r.name||r.description||r.payee||'Recurring item',r.type||'expense',nameOf(r.category,safeCategories),
    num(r.amount),r.frequency||r.period||'monthly',iso(r.nextDate||r.nextOccurrence||r.startDate||'')||'',
    r.isActive===false?'Paused':'Active']);
 const taxExpense=posted.filter(t=>t.type==='expense').sort((a,b)=>iso(a.date).localeCompare(iso(b.date)));
 const peopleRows=people.filter(p=>p&&!p.isDeleted).map(p=>{
   const ledger=personLedger(p,allAccounts,ious,new Date(asOfCutoff+'T12:00:00'));
   const active=ledger.ledgerEntries||[];
   const debts=active.filter(x=>x.entryType==='iou');
   return [p.name||'Person',active.length,debts.length,ledger.currentBalance,
     ledger.currentBalance>0?'Receivable':ledger.currentBalance<0?'Payable':'Settled'];
 });
 const transactionRows=[...selected].sort((a,b)=>iso(a.date).localeCompare(iso(b.date)))
   .map(t=>[iso(t.date),t.type,nameOf(t.category,safeCategories),t.payee||'',
     t.type==='expense'?-num(t.amount):num(t.amount),t.account||'',t.person||'',
     t.status||'',t.isVirtualAllocation?'Accrual allocation':t.notes||'']);
 const taxRows=taxExpense.map(t=>[iso(t.date),t.payee||'',nameOf(t.category,safeCategories),
   num(t.amount),t.account||'',t.notes||'']);
 const result=[
  asReport('transactions',['Date','Type','Category','Payee','Amount','Account','Person','Status','Notes'],transactionRows,{
    kpis:[['Income',totals.income],['Expenses',totals.expenses],['Net',totals.net],['Entries',totals.transactions]],
    chart:monthChart,notes:['Accounting basis: '+basis+'. IOU-linked transfers are excluded from operating totals.',
      view==='future'?'Future scheduled projections are not confirmed payments.':'Only recognised entries up to the report as-of date are included.']}),
  asReport('recurring',['Name','Type','Category','Amount','Frequency','Next date','Status'],recurringRows,{
    kpis:[['Active rules',recurringRows.filter(r=>r[6]==='Active').length],['All rules',recurringRows.length]],
    notes:['This is a schedule register, not proof that a payment was posted. Amounts are per rule frequency.']}),
  asReport('categories',['Category','Type','Entries','Period amount','Colour'],categoryRows,{
    kpis:[['Categories',categoryRows.length],['Expense categories',categoryRows.filter(r=>r[1]==='expense').length]],
    chart:spendChart,notes:['Category totals reflect the selected accounting basis and date range.']}),
  asReport('budget',['Category','Budget period','Budget','Actual','Remaining','Used %'],budgetRows,{
    kpis:[['Budget',budgetLimit],['Recognised spend',budgetUsed],['Remaining',fromCents(toCents(budgetLimit)-toCents(budgetUsed))],
      ['Used %',budgetLimit?Math.round(budgetUsed/budgetLimit*1000)/10:0]],
    chart:budgetChart,notes:['Budget allocations and actual spending use the accrual basis regardless of the selected ledger accounting mode.',
      'Budget limits are allocated to months using existing fiscal/custom-period rules. Future scheduled costs are not counted as actual.']}),
  asReport('spending',['Category','Amount','Share %','Entries'],expCategory.map(x=>
    [x.name,x.amount,spentTotal?Math.round(x.amount/spentTotal*1000)/10:0,x.count]),{
    kpis:[['Expenses',spentTotal],['Categories',expCategory.length]],
    chart:spendChart,notes:['Spending breakdown uses recognised expenses for the selected period.']}),
  asReport('monthly',['Month','Income','Expenses','Net','Entries'],monthRows,{
    kpis:[['Income',totals.income],['Expenses',totals.expenses],['Net position',totals.net],
      ['Budget headroom',fromCents(toCents(budgetLimit)-toCents(budgetUsed))]],
    chart:monthChart,
    dashboard:{daily:dailyChart.length?dailyChart:monthChart,spending:spendChart,budget:budgetRows,
      transactions:transactionRows,recurring:recurringRows},
    notes:['Financial summary uses recognised entries under the selected accounting basis.',
      'Recurring rule amounts are commitments, not proof of payment.']}),
  asReport('cashflow',['Month','Cash received','Cash paid','Cash net','Payments'],cashMonths,{
    kpis:[['Cash received',addMoney(cashMonths,r=>r[1])],['Cash paid',addMoney(cashMonths,r=>r[2])],
      ['Net movement',addMoney(cashMonths,r=>r[3])]],
    chart:cashMonths.map(r=>({name:r[0],primary:r[1],secondary:r[2]})),
    notes:['Cash flow includes only posted transactions; daily accrual allocations and IOU-linked transfers are excluded.',
      'Not a bank statement or reconciliation.']}),
  asReport('tax',['Date','Payee','Category','Amount','Account','Notes'],taxRows,{
    kpis:[['Posted expenses',addMoney(taxExpense)],['Expense records',taxRows.length]],
    chart:spendChart,
    notes:['Tax preparation register only. No expense is classified as tax-deductible automatically.',
      'Confirm deductions, GST treatment and compliance requirements with your qualified adviser.']}),
  asReport('people',['Person','Entries to date','IOUs to date','Balance','Position'],peopleRows,{
    kpis:[['People',peopleRows.length],['Receivable',addMoney(peopleRows.filter(r=>r[3]>0),r=>r[3])],
      ['Payable',addMoney(peopleRows.filter(r=>r[3]<0),r=>Math.abs(r[3]))]],
    notes:['Balances are cumulative through '+asOfCutoff+' and are not restricted to the report period.',
      'Positive balance means money owed to you according to recorded transactions and IOUs.']}),
  asReport('trend',['Month','Income','Expenses','Net','Entries'],monthRows,{
    kpis:[['Income',totals.income],['Expenses',totals.expenses],['Net',totals.net]],
    chart:monthChart,notes:['The trend is calculated from the same accounting entries used in the dashboard.']})
 ];
 return {reports:result,totals,period:{start:from,end:to},currency:settings.currency||'AUD',
   accountingMode:basis,view,account:account||'All accounts',generatedAt:new Date()};
}

// CSV: numeric amounts remain numeric. Prefix text that spreadsheet programs
// might execute as a formula, protecting payee/notes supplied by customers.
const csvField=value=>{
 if(value===null||value===undefined)return '""';
 if(typeof value==='number'&&Number.isFinite(value))return String(value);
 let text=String(value);
 if(/^[\s]*[=+@-]/.test(text))text="'"+text;
 return '"'+text.replace(/"/g,'""')+'"';
};
export function createFinancialReportCsv(report){
 if(!report||!Array.isArray(report.columns)||!Array.isArray(report.rows))throw new Error('Invalid report');
 return '\uFEFF'+[report.columns,...report.rows].map(row=>row.map(csvField).join(',')).join('\r\n')+'\r\n';
}


// Self-contained vector PDF writer: no online CDN, printer dialog, server,
// external PDF dependency, customer-data upload or browser permissions needed.
// Uses Helvetica standard fonts, with text normalised to WinAnsi-safe ASCII.
const A4_W=595.28,A4_H=841.89;
const pdfRGB=hex=>{
 const value=String(hex).replace('#','');
 return [0,2,4].map(i=>Number.parseInt(value.slice(i,i+2),16)/255).map(x=>Number.isFinite(x)?x:0);
};
const pdfColour=hex=>pdfRGB(hex).map(n=>n.toFixed(3)).join(' ');
const pdfSafe=value=>String(value??'').normalize('NFKD')
 .replace(/[\u0300-\u036f]/g,'').replace(/[\u2010-\u2015]/g,'-')
 .replace(/[\u2018\u2019]/g,"'").replace(/[\u201C\u201D]/g,'"')
 .replace(/[^\x20-\x7e]/g,'?');
const pdfEsc=value=>pdfSafe(value).replace(/\\/g,'\\\\').replace(/\(/g,'\\(').replace(/\)/g,'\\)');
function createPdfCanvas(){
 const pages=[];
 const page=()=>{const content=[];pages.push(content);return content};
 const rectangle=(commands,x,top,width,height,fill='#ffffff',stroke=null)=>{
   if(fill)commands.push(pdfColour(fill)+' rg '+x.toFixed(2)+' '+(A4_H-top-height).toFixed(2)+' '+width.toFixed(2)+' '+height.toFixed(2)+' re f');
   if(stroke)commands.push(pdfColour(stroke)+' RG 0.6 w '+x.toFixed(2)+' '+(A4_H-top-height).toFixed(2)+' '+width.toFixed(2)+' '+height.toFixed(2)+' re S');
 };
 const line=(commands,x1,y1,x2,y2,stroke='#e2e8f0',thick=0.7)=>
   commands.push(pdfColour(stroke)+' RG '+thick+' w '+x1.toFixed(2)+' '+(A4_H-y1).toFixed(2)+' m '+x2.toFixed(2)+' '+(A4_H-y2).toFixed(2)+' l S');
 const text=(commands,value,x,top,size=10,colour='#032d60',bold=false,width=0,align='left')=>{
   let str=pdfSafe(value);
   if(width){
     const allowance=Math.max(1,Math.floor(width/(size*(bold?0.57:0.52))));
     if(str.length>allowance)str=str.slice(0,Math.max(0,allowance-3))+'...';
     if(align==='right')x+=Math.max(0,width-str.length*size*(bold?0.57:0.52));
   }
   commands.push('BT '+pdfColour(colour)+' rg /'+(bold?'F2':'F1')+' '+size.toFixed(1)+
     ' Tf 1 0 0 1 '+x.toFixed(2)+' '+(A4_H-top-size).toFixed(2)+' Tm ('+pdfEsc(str)+') Tj ET');
 };
 const footer=(commands,n,total)=>{
   line(commands,38,802,557,802,'#dae4f2');
   rectangle(commands,38,811,17,17,'#0176d3');
   text(commands,'$',43,813,12,'#ffffff',true);
   text(commands,'Generated from Expense Manager Pro',62,815,9,'#68809e');
   text(commands,'Page '+n+' of '+total,480,815,9,'#68809e',false,76,'right');
 };
 const bytes=()=>{
   for(let i=0;i<pages.length;i++)footer(pages[i],i+1,pages.length);
   // Fixed object numbers: 1 Catalog, 2 Pages, 3/4 Font, then page/stream pairs.
   const objects=['',
     '<< /Type /Catalog /Pages 2 0 R >>','',
     '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >>',
     '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold /Encoding /WinAnsiEncoding >>'];
   const kids=[];
   pages.forEach(commands=>{
     const pageId=objects.length,streamId=pageId+1;
     kids.push(pageId+' 0 R');
     objects.push('<< /Type /Page /Parent 2 0 R /MediaBox [0 0 '+A4_W+' '+A4_H+'] /Resources << /Font << /F1 3 0 R /F2 4 0 R >> >> /Contents '+streamId+' 0 R >>');
     const stream=commands.join('\n')+'\n';
     objects.push('<< /Length '+new TextEncoder().encode(stream).length+' >>\nstream\n'+stream+'endstream');
   });
   objects[2]='<< /Type /Pages /Count '+pages.length+' /Kids [ '+kids.join(' ')+' ] >>';
   let pdf='%PDF-1.4\n%\xD2\xD3\xD4\xD5\n',offsets=[0];
   // Source is ASCII only, so character positions equal UTF-8 byte offsets
   // except the binary-comment marker encoded explicitly as four bytes below.
   const encoder=new TextEncoder();
   const chunks=[encoder.encode('%PDF-1.4\n%'),Uint8Array.from([0xD2,0xD3,0xD4,0xD5]),encoder.encode('\n')];
   let length=chunks.reduce((n,c)=>n+c.length,0);
   for(let id=1;id<objects.length;id++){
     offsets.push(length);
     const part=encoder.encode(id+' 0 obj\n'+objects[id]+'\nendobj\n');
     chunks.push(part);length+=part.length;
   }
   const startXref=length;
   let xref='xref\n0 '+objects.length+'\n0000000000 65535 f \n';
   for(let id=1;id<objects.length;id++)xref+=String(offsets[id]).padStart(10,'0')+' 00000 n \n';
   xref+='trailer\n<< /Size '+objects.length+' /Root 1 0 R >>\nstartxref\n'+startXref+'\n%%EOF\n';
   chunks.push(encoder.encode(xref));
   const out=new Uint8Array(chunks.reduce((n,c)=>n+c.length,0));
   let position=0;chunks.forEach(c=>{out.set(c,position);position+=c.length});
   return out;
 };
 return {pages,page,rectangle,line,text,bytes};
}
function pdfMoney(value,currency='AUD'){
 const number=num(value);
 return (number<0?'-':'')+currency+' '+Math.abs(number).toLocaleString('en-AU',{minimumFractionDigits:2,maximumFractionDigits:2});
}
function pdfValue(value,header,currency){
 if(value===null||value===undefined)return '-';
 if(typeof value!=='number')return String(value);
 if(/%/.test(header))return value.toFixed(1)+'%';
 if(/entries|rules|months|people|records|count|payments|categories|io[u]?s/i.test(header))return String(value);
 return pdfMoney(value,currency);
}
function pdfReport(doc,report,model){
 const {page,rectangle:rect,line,text}=doc;
 const margin=38,width=519;
 const primary='#032d60',blue='#0176d3',muted='#64748b';
 const title=report.name,subtitle=model.period.start+' to '+model.period.end;
 let out=page(),y=0;
 const header=(continued=false)=>{
   rect(out,0,0,A4_W,112,primary);
   rect(out,0,0,8,112,blue);
   rect(out,470,0,125,112,'#0b4078');
   rect(out,38,22,28,28,blue);
   text(out,'$',46,25,18,'#ffffff',true);
   text(out,'BPA SOLUTIONS',77,26,13,'#ffffff',true);
   text(out,'Expense Manager Pro 2.0',77,44,8,'#c4def8');
   text(out,continued?title+' (continued)':title,38,66,20,'#ffffff',true,495);
   text(out,subtitle+'  |  '+model.account+'  |  '+model.accountingMode.toUpperCase(),38,94,8,'#e3efff',false,520);
   y=130;
 };
 header();
 text(out,'FINANCIAL REPORT',margin,y,9,blue,true);
 text(out,'Selected '+model.view+' view  |  '+model.currency+'  |  Generated '+format(model.generatedAt,'d MMM yyyy HH:mm'),margin,y+16,9,muted);
 y+=43;
 const cards=report.kpis.slice(0,4);
 if(cards.length){
   const gap=9,cardW=(width-(cards.length-1)*gap)/cards.length;
   cards.forEach(([label,value],index)=>{
     const x=margin+index*(cardW+gap);
     rect(out,x,y,cardW,80,index%2===0?'#f0f6ff':'#f5f8fc','#dbe7f5');
     rect(out,x,y,3,80,index===1?'#fb7185':'#0176d3');
     text(out,label.toUpperCase(),x+11,y+12,8,muted,true,cardW-20);
     text(out,pdfValue(value,label,model.currency),x+11,y+35,Math.min(15,cardW>125?14:12),primary,true,cardW-20);
   });
   y+=97;
 }
 if(report.chart?.length){
   rect(out,margin,y,width,214,'#ffffff','#dbe7f5');
   text(out,report.id==='budget'?'BUDGET VS ACTUAL':report.id==='spending'||report.id==='categories'||report.id==='tax'?'SPENDING DISTRIBUTION':'INCOME VS EXPENSES',margin+15,y+12,11,primary,true);
   const chart=report.chart.slice(0,7);
   const top=y+52,h=106,max=Math.max(1,...chart.flatMap(r=>[num(r.primary),num(r.secondary)]));
   const plotX=margin+29,plotWidth=width-60,base=top+h;
   [0,0.5,1].forEach(f=>{
     const yy=base-h*f;
     line(out,plotX,yy,plotX+plotWidth,yy,'#e6edf5');
     text(out,f===0?'0':f===1?'MAX':'',plotX-18,yy-6,7,muted);
   });
   const cell=plotWidth/Math.max(1,chart.length),bar=Math.min(21,cell/3);
   chart.forEach((r,i)=>{
     const x=plotX+i*cell+cell/2;
     const a=Math.max(0,num(r.primary))/max*h,b=Math.max(0,num(r.secondary))/max*h;
     rect(out,x-(r.secondary==null?bar/2:bar+2),base-a,bar,a,'#60a5fa');
     if(r.secondary!=null)rect(out,x+2,base-b,bar,b,'#34cfa1');
     text(out,r.name,plotX+i*cell,base+10,7,muted,false,cell-3);
   });
   text(out,report.chart.some(r=>r.secondary!=null)?'BLUE: INCOME / BUDGET      GREEN: EXPENSES / ACTUAL':'BLUE: CATEGORY TOTAL',margin+15,y+185,8,muted);
   y+=229;
 }
 if(report.notes?.length){
   const maxNote=report.notes.slice(0,2);
   const noteH=15+maxNote.length*16;
   rect(out,margin,y,width,noteH,'#f6f9fd','#e0e9f5');
   maxNote.forEach((note,i)=>text(out,(i+1)+'. '+note,margin+10,y+9+i*16,8,'#526a88',false,width-22));
   y+=noteH+15;
 }
 const indexes=report.columns.length>6?
   report.id==='transactions'?[0,1,2,3,4,5]:[0,1,2,3,4,5]:
   report.columns.map((_,i)=>i);
 const cols=indexes.map(i=>report.columns[i]);
 const textLength=cols.map(c=>Math.max(7,Math.min(20,String(c).length+3)));
 const weights=cols.map((c,i)=>/category|payee|name|person|description/i.test(c)?1.35:/amount|actual|expense|income|budget|net|remaining|balance/i.test(c)?1.2:1);
 const totalW=weights.reduce((a,b)=>a+b,0);
 const widths=weights.map(w=>width*w/totalW);
 function tableHead(){
   if(y>744){out=page();header(true)}
   text(out,'DETAILS',margin,y,11,primary,true);
   y+=21;
   rect(out,margin,y,width,28,'#e9f1ff');
   let xx=margin;cols.forEach((c,i)=>{text(out,c,xx+8,y+8,8,primary,true,widths[i]-12);xx+=widths[i]});
   y+=28;
 }
 tableHead();
 const rows=report.rows.length?report.rows:[['No records in selected period']];
 report.rows.length?rows.forEach((row,index)=>{
   if(y+23>789){out=page();header(true);tableHead()}
   rect(out,margin,y,width,23,index%2===0?'#ffffff':'#f7faff');
   let x=margin;
   cols.forEach((c,i)=>{
     text(out,pdfValue(row[indexes[i]],c,model.currency),x+7,y+6,8,'#2f4969',false,widths[i]-11);
     x+=widths[i];
   });
   line(out,margin,y+23,margin+width,y+23,'#edf2f8',0.4);
   y+=23;
 }):(
   text(out,'No records were found for this selected report period.',margin+11,y+12,10,muted),
   y+=35
 );
 if(y+35<790){
   y+=13;text(out,'DATA SOURCE',margin,y,8,blue,true);
   text(out,'Derived from your current Expense Manager workspace. No Google Sheets or backend writes.',margin,y+14,8,muted);
 }
}

// Premium one-page executive summary using ONLY the existing report model.
// All graphics and text are vector PDF objects, with no canvas screenshots.
function pdfMonthlyDashboard(doc,report,model){
 const {page,rectangle:rect,line,text}=doc,out=page(),dashboard=report.dashboard||{};
 const m=38,navy='#032d60',blue='#0176d3',muted='#64748b';
 const currency=model.currency;
 const panel=(x,y,w,h,label)=>{
   rect(out,x,y,w,h,'#ffffff','#dbe7f5');
   rect(out,x,y,3,h,'#e3efff');
   text(out,label,x+12,y+12,11,navy,true,w-24);
 };
 // Cover, period and branding.
 rect(out,0,0,A4_W,111,navy);
 rect(out,0,0,8,111,blue);
 rect(out,408,0,187,111,'#0b4078');
 rect(out,m,20,30,30,blue);
 text(out,"$",47,23,18,'#ffffff',true);
 text(out,'BPA SOLUTIONS',79,24,14,'#ffffff',true);
 text(out,'Expense Manager Pro 2.0',79,42,8,'#cae1ff');
 text(out,'Monthly Financial Summary',m,67,22,'#ffffff',true,515);
 text(out,model.period.start+' to '+model.period.end+'   |   '+model.accountingMode.toUpperCase()+'   |   '+currency,m,96,8,'#d9eafb');
 text(out,'EXECUTIVE SUMMARY',m,126,10,blue,true);
 text(out,'Recognised financial activity for the selected period',m,143,9,muted);
 const budget=report.kpis.find(x=>x[0]==='Budget headroom')?.[1]||0;
 const cards=[
  ['TOTAL INCOME',model.totals.income,'#ebfaf2','#10b981'],
  ['TOTAL EXPENSES',model.totals.expenses,'#fff1f2','#fb7185'],
  ['NET POSITION',model.totals.net,'#edf4ff','#0176d3'],
  ['BUDGET HEADROOM',budget,'#f4f0ff','#8b5cf6']
 ];
 cards.forEach(([label,value,background,accent],i)=>{
   const gap=8,w=(519-3*gap)/4,x=m+i*(w+gap);
   rect(out,x,163,w,75,background,'#e2e8f0');
   rect(out,x,163,3,75,accent);
   text(out,label,x+10,178,8,muted,true,w-20);
   text(out,pdfMoney(value,currency),x+10,201,12,navy,true,w-18);
 });
 // Compact, explanatory trend chart with two actual numeric series.
 panel(m,251,295,200,'Income vs Expenses');
 panel(345,251,212,200,'Spending by Category');
 const trend=(dashboard.daily||[]).slice(0,32);
 const max=Math.max(1,...trend.flatMap(x=>[num(x.primary),num(x.secondary)]));
 const base=405,barHeight=107,chartX=m+22,chartW=255;
 [0,0.5,1].forEach(r=>{
   const yy=base-r*barHeight;
   line(out,chartX,yy,chartX+chartW,yy,'#e7eff7');
   if(r>0)text(out,(max*r).toLocaleString('en-AU',{maximumFractionDigits:0}),chartX,yy-12,7,muted);
 });
 if(!trend.length)text(out,'No financial activity in the selected period',m+20,342,8,muted);
 trend.forEach((entry,i)=>{
   const slot=chartW/trend.length,x=chartX+i*slot+slot/2;
   const bw=Math.min(8,Math.max(2,slot*.28));
   const income=Math.max(0,num(entry.primary))/max*barHeight;
   const expense=Math.max(0,num(entry.secondary))/max*barHeight;
   rect(out,x-bw-0.5,base-income,bw,income,'#34d399');
   rect(out,x+0.5,base-expense,bw,expense,'#fb7185');
   if(i%(trend.length>21?7:trend.length>10?4:1)===0)text(out,entry.name,x-6,415,7,muted);
 });
 text(out,'GREEN  Income      RED  Expenses',m+17,435,8,muted);
 const spending=(dashboard.spending||[]).slice(0,5),spendTotal=spending.reduce((n,r)=>n+num(r.primary),0);
 const colours=['#fb7185','#60a5fa','#fbbf24','#a78bfa','#34d399'];
 if(!spending.length)text(out,'No expenses to display',358,325,8,muted);
 spending.forEach((item,i)=>{
   const y=298+i*28,share=spendTotal?num(item.primary)/spendTotal:0;
   rect(out,357,y+4,7,7,colours[i]);
   text(out,item.name,371,y+1,8,navy,true,97);
   text(out,(share*100).toFixed(1)+'%',497,y+1,8,muted,true,44,'right');
   rect(out,371,y+19,153,5,'#ecf2f9');
   rect(out,371,y+19,153*share,5,colours[i]);
 });
 // Budget performance and actual recorded transactions.
 panel(m,463,295,178,'Budget Performance');
 panel(345,463,212,178,'Top Transactions');
 text(out,'CATEGORY',m+12,491,8,muted,true);
 text(out,'BUDGET',m+137,491,8,muted,true);
 text(out,'ACTUAL',m+219,491,8,muted,true);
 line(out,m+12,510,m+285,510,'#e4ebf5');
 const budgetRows=(dashboard.budget||[]).slice(0,5);
 if(!budgetRows.length)text(out,'No budgets configured',m+12,521,8,muted);
 budgetRows.forEach((r,i)=>{
   const y=517+i*22;
   text(out,r[0],m+12,y,8,navy,true,117);
   text(out,pdfMoney(r[2],currency),m+137,y,7,muted,false,77);
   text(out,pdfMoney(r[3],currency),m+219,y,7,muted,false,68);
 });
 const transactions=(dashboard.transactions||[]).slice()
   .sort((a,b)=>Math.abs(num(b[4]))-Math.abs(num(a[4]))).slice(0,5);
 if(!transactions.length)text(out,'No transactions in period',357,520,8,muted);
 transactions.forEach((r,i)=>{
   const y=492+i*26;
   text(out,r[3]||r[2],357,y,8,navy,true,107);
   text(out,pdfMoney(r[4],currency),466,y,8,num(r[4])<0?'#e11d48':'#059669',true,81,'right');
   text(out,r[2]+'  |  '+r[0],357,y+12,7,muted,false,187);
   line(out,357,y+24,545,y+24,'#edf2f7',0.4);
 });
 // Recurring commitments are schedules, not evidence of posted payments.
 panel(m,652,250,129,'Recurring Commitments');
 panel(301,652,256,129,'Insights & Highlights');
 const recurring=(dashboard.recurring||[]).slice(0,4);
 if(!recurring.length)text(out,'No recurring commitments',m+12,695,8,muted);
 recurring.forEach((r,i)=>{
   const y=682+i*22;
   text(out,r[0],m+12,y,8,navy,true,126);
   text(out,pdfMoney(r[3],currency)+' / '+r[4],m+139,y,7,muted,false,95,'right');
 });
 const budgetLimit=(dashboard.budget||[]).reduce((n,r)=>n+num(r[2]),0);
 const budgetSpent=(dashboard.budget||[]).reduce((n,r)=>n+num(r[3]),0);
 const facts=[
  ['Net position',pdfMoney(model.totals.net,currency)],
  ['Budget utilisation',budgetLimit?(budgetSpent/budgetLimit*100).toFixed(1)+'%':'No budget configured'],
  ['Top expense category',spending[0]?.name||'None recorded']
 ];
 facts.forEach(([label,value],i)=>{
   const y=682+i*31;
   rect(out,312,y-2,233,28,i%2?'#f1f6ff':'#f8faff');
   text(out,label,320,y+1,8,navy,true,211);
   text(out,value,320,y+13,8,muted,false,213);
 });
}
export function createFinancialReportPdf(model,reportIds=null){
 const picked=reportIds?model.reports.filter(r=>reportIds.includes(r.id)):model.reports;
 if(!picked.length)throw new Error('Choose a report to export.');
 const pdf=createPdfCanvas();
 picked.forEach(r=>{
   if(r.id==='monthly'&&r.dashboard){
     pdfMonthlyDashboard(pdf,r,model);
     if(r.rows.length>1)pdfReport(pdf,r,model);
   }else pdfReport(pdf,r,model);
 });
 return pdf.bytes();
}
// A ZIP with independent, machine-readable CSV files. "Export all" is one
// browser download, never a burst of ten downloads blocked by the browser.
const zipCrc=bytes=>{
 let crc=0xffffffff;
 for(const byte of bytes){
   crc^=byte;
   for(let i=0;i<8;i++)crc=crc&1?(crc>>>1)^0xedb88320:crc>>>1;
 }
 return (crc^0xffffffff)>>>0;
};
const le16=(v)=>Uint8Array.from([v&255,(v>>>8)&255]);
const le32=(v)=>Uint8Array.from([v&255,(v>>>8)&255,(v>>>16)&255,(v>>>24)&255]);
export function createAllReportsCsvZip(model){
 const enc=new TextEncoder(),sections=[],directory=[];
 let offset=0;
 for(const report of model.reports){
   const name=enc.encode('expense-'+report.id+'.csv'),data=enc.encode(createFinancialReportCsv(report)),crc=zipCrc(data);
   const local=new Uint8Array([
     ...le32(0x04034b50),...le16(20),...le16(0x0800),...le16(0),...le16(0),...le16(0),
     ...le32(crc),...le32(data.length),...le32(data.length),...le16(name.length),...le16(0),...name
   ]);
   sections.push(local,data);
   const central=new Uint8Array([
     ...le32(0x02014b50),...le16(20),...le16(20),...le16(0x0800),...le16(0),
     ...le16(0),...le16(0),...le32(crc),...le32(data.length),...le32(data.length),
     ...le16(name.length),...le16(0),...le16(0),...le16(0),...le16(0),
     ...le32(0),...le32(offset),...name
   ]);
   directory.push(central);offset+=local.length+data.length;
 }
 const dirLength=directory.reduce((n,d)=>n+d.length,0);
 const end=new Uint8Array([...le32(0x06054b50),...le16(0),...le16(0),
   ...le16(directory.length),...le16(directory.length),...le32(dirLength),...le32(offset),
   ...le16(0)]);
 const parts=[...sections,...directory,end];
 const result=new Uint8Array(parts.reduce((n,p)=>n+p.length,0));
 let at=0;parts.forEach(p=>{result.set(p,at);at+=p.length});
 return result;
}
function downloadOnce(bytes,mime,filename){
 if(typeof document==='undefined'||!globalThis.URL?.createObjectURL)throw new Error('Browser downloads are not available.');
 const blob=new Blob([bytes],{type:mime});
 const url=URL.createObjectURL(blob);
 try{
   const a=document.createElement('a');
   a.href=url;a.download=filename;a.style.display='none';
   document.body.appendChild(a);
   a.click();a.remove();
 }finally{setTimeout(()=>URL.revokeObjectURL(url),1500)}
 return filename;
}
export function downloadFinancialReport(model,id,kind){
 const file=String(id||'all').replace(/[^a-z0-9_-]/gi,'');
 const suffix=model.period.start+'_'+model.period.end;
 if(kind==='csv'){
   if(id==='all')return downloadOnce(createAllReportsCsvZip(model),'application/zip',
     'expense-manager-all-reports-'+suffix+'.zip');
   const report=model.reports.find(r=>r.id===id);
   if(!report)throw new Error('Unknown report selected.');
   return downloadOnce(createFinancialReportCsv(report),'text/csv;charset=utf-8',
     'expense-manager-'+file+'-'+suffix+'.csv');
 }
 if(kind==='pdf')return downloadOnce(createFinancialReportPdf(model,id==='all'?null:[id]),
   'application/pdf','expense-manager-'+file+'-'+suffix+'.pdf');
 throw new Error('Choose CSV or PDF.');
}